import { useState, type ComponentProps } from 'react';

import { parseMoneyInput, toMajorString, type Money } from '@nucleus-commerce/core';
import { Input } from '@nucleus-commerce/ui/components/input';
import { cn } from '@nucleus-commerce/ui/lib/utils';

export interface MoneyInputProps extends Omit<
  ComponentProps<typeof Input>,
  'value' | 'onChange' | 'type'
> {
  /** Integer minor units, as the API holds it. */
  value: Money | null;
  onChange: (value: Money | null) => void;
  currency: string;
}

const toText = (value: Money | null) => (value ? toMajorString(value) : '');

const same = (a: Money | null, b: Money | null) =>
  a?.amount === b?.amount && a?.currency === b?.currency;

/**
 * Text input for money. The person sees and types major units ("1,299.5"); the form value
 * stays integer minor units. Every keystroke that parses is committed, so a save bar or a button
 * knows at once; the raw text stays until blur, when "1,299.5" reads back as "1299.50". Text that
 * does not parse puts back the value from before the typing and is marked invalid on blur.
 * Use with react-hook-form's <Controller>.
 */
export function MoneyInput({
  value,
  onChange,
  currency,
  className,
  onBlur,
  ...rest
}: MoneyInputProps) {
  const [text, setText] = useState(() => toText(value));
  const [invalid, setInvalid] = useState(false);
  // What unparseable text falls back to: the value before the person started typing.
  const [settled, setSettled] = useState(value);
  const [typing, setTyping] = useState(false);
  // "Adjust state when a prop changes" pattern: resync only when the prop itself moves (form
  // reset, server round trip) to something the person did not just commit, so typing is not
  // reformatted under the caret and a pending save does not flash the old amount.
  const incomingAmount = value?.amount ?? null;
  const [seenAmount, setSeenAmount] = useState(incomingAmount);
  const [committedAmount, setCommittedAmount] = useState(incomingAmount);
  if (incomingAmount !== seenAmount) {
    setSeenAmount(incomingAmount);
    // A parent may answer an emptied input with a value of its own (no price means 0). That waits
    // for blur, or the next digit would land after it.
    if (incomingAmount !== committedAmount && (!typing || text.trim() !== '')) {
      setCommittedAmount(incomingAmount);
      setSettled(value);
      setText(toText(value));
      setInvalid(false);
    }
  }

  const commit = (next: Money | null) => {
    setCommittedAmount(next?.amount ?? null);
    if (!same(next, value)) onChange(next);
  };

  return (
    <div className="relative">
      <span
        className="text-muted-foreground pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm"
        aria-hidden
      >
        {currency}
      </span>
      <Input
        {...rest}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={text}
        aria-invalid={invalid || rest['aria-invalid']}
        className={cn('pl-14 text-right tabular-nums', className)}
        onChange={(e) => {
          setText(e.target.value);
          setTyping(true);
          setInvalid(false);
          const trimmed = e.target.value.trim();
          commit(trimmed === '' ? null : (parseMoneyInput(trimmed, currency) ?? settled));
        }}
        onBlur={(e) => {
          // The DOM holds the latest keystrokes even when React has not re-rendered yet
          // (a blur that follows the last input event in the same task).
          const trimmed = e.target.value.trim();
          setTyping(false);
          if (trimmed === '') {
            setText('');
            setSettled(null);
            // Look at the prop again: a parent's own answer to empty now shows.
            setSeenAmount(null);
            commit(null);
          } else {
            const parsed = parseMoneyInput(trimmed, currency);
            if (parsed) {
              setText(toMajorString(parsed));
              setSettled(parsed);
              commit(parsed);
            } else {
              setInvalid(true);
            }
          }
          onBlur?.(e);
        }}
      />
    </div>
  );
}
